import { api } from './api';
import type {
  ValidationRun,
  ValidationSummary,
  TriggerValidationRequest,
  BulkValidationRequest,
  BulkValidationResponse,
  ValidationHistoryFilters,
} from '../types/validation.types';

interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

export const validationService = {
  // Trigger validation for a single page
  async triggerValidation(request: TriggerValidationRequest): Promise<ValidationRun> {
    const response = await api.post<ValidationRun>('/coreapi/validation/runs', request);
    return response.data;
  },

  // Get validation run by ID
  async getValidationRun(runId: number, severityFilter?: string): Promise<ValidationRun> {
    const params = severityFilter ? { severity: severityFilter } : {};
    const response = await api.get<ValidationRun>(`/coreapi/validation/runs/${runId}`, { params });
    return response.data;
  },

  // Get validation history for a page
  async getValidationHistory(pageId: number, filters?: ValidationHistoryFilters): Promise<PaginatedResponse<ValidationRun>> {
    const response = await api.get<PaginatedResponse<ValidationRun>>(
      `/coreapi/pages/${pageId}/validation/history`,
      { params: filters }
    );
    return response.data;
  },

  // Get validation history for a site
  async getSiteValidationHistory(siteId: number, filters?: ValidationHistoryFilters): Promise<PaginatedResponse<ValidationRun>> {
    const response = await api.get<PaginatedResponse<ValidationRun>>(
      `/customer/sites/${siteId}/validation/history`,
      { params: filters }
    );
    return response.data;
  },

  // Trigger bulk validation
  async triggerBulkValidation(request: BulkValidationRequest): Promise<BulkValidationResponse> {
    const response = await api.post<BulkValidationResponse>(
      `/sites/${request.siteId}/validation/bulk`,
      request
    );
    return response.data;
  },

  // Get validation summary
  async getValidationSummary(siteId: number): Promise<ValidationSummary> {
    const response = await api.get<ValidationSummary>(`/sites/${siteId}/validation/summary`);
    return response.data;
  },

  // Rerun validation
  async rerunValidation(runId: number): Promise<ValidationRun> {
    const response = await api.post<ValidationRun>(`/coreapi/validation/runs/${runId}/retry`);
    return response.data;
  },

  // Get all validation runs (with pagination)
  async getAllValidations(filters?: {
    siteId?: number;
    status?: string;
    page?: number;
    size?: number;
  }): Promise<PaginatedResponse<ValidationRun>> {
    const response = await api.get<PaginatedResponse<ValidationRun>>('/validation/runs', {
      params: filters,
    });
    return response.data;
  },
};
